import React, { useState, useEffect } from "react";
import ParentPanel from "../panel/ParentPanel";
import "../styles/ParentsDashboard.css";
import studentPhoto from "../icons/student-photo.png";
import schoolPhoto from "../icons/school-photo.jpg";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { FaRupeeSign, FaBook, FaBus, FaBell, FaClipboardList, FaLink } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Calendar } from "../../../components/ui/calendar";
import api from "../../../api/api";

// ✅ ICON MAP
import dashboardIcon from "../icons/dashboard.png";
import classIcon from "../icons/class.png";
import feesIcon from "../icons/fees.png";
import ticketIcon from "../icons/ticket.png";
import timetableIcon from "../icons/timetable.png";
import noticeIcon from "../icons/notice.png";
import resultsIcon from "../icons/results.png";
import transportIcon from "../icons/transport.png";
import examIcon from "../icons/exam.png";
import homeIcon from "../icons/homework.svg";
import class1Icon from "../icons/class.svg";
import assignmentIcon from "../icons/assignment.svg";
import booksIcon from "../icons/books.svg";

const iconMap = {
  "Parent Dashboard": dashboardIcon,
  "Join Live Class": classIcon,
  "Pay Fees": feesIcon,
  "School Timetable": timetableIcon,
  "Home Work": homeIcon,
  "Class Work": class1Icon,
  "Assignments": assignmentIcon,
  "See Notice": noticeIcon,
  "See Results": resultsIcon,
  "Online Exam": examIcon,
  "See Booklist": booksIcon,
  "Transport": transportIcon,
  "Raise a Ticket": ticketIcon,
};

// ✅ COLOR MAP
const colorMap = {
  "Parent Dashboard": "#e55646ff",
  "Join Live Class": "#4f46e5",
  "Pay Fees": "#e91e63",
  "Raise a Ticket": "#f44336",
  "School Timetable": "#ff9800",
  "See Notice": "#4caf50",
  "See Results": "#2196f3",
  "Transport": "#ff5722",
  "Online Exam": "#8e24aa",
  "Home Work": "#9c27b0",
  "Class Work": "#009688",
  "Assignments": "#3f51b5",
  "See Booklist": "#63a660ff",
};

const ParentsDashboard = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [date, setDate] = useState(new Date());
  const [notifications, setNotifications] = useState([
    { id: 1, text: "Math exam schedule released" },
    { id: 2, text: "Science project submission tomorrow" },
    { id: 3, text: "PTA meeting on 25th August" },
  ]);
  const [showNotifications, setShowNotifications] = useState(false);

  const [modules, setModules] = useState([]);
  const navigate = useNavigate();

const studentId = localStorage.getItem("userId");
const token = localStorage.getItem("token");
const schoolId = localStorage.getItem("schoolId") || "";
useEffect(() => {

  if (!token) {
    console.warn("No token found");
    return;
  }

  if (!schoolId) {
    console.warn("No schoolId found in localStorage");
    return;
  }

  const fetchModules = async () => {
    try {

      const res = await api.get(`/api/modules/${schoolId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Parent modules:", res.data); // helpful debug

      const enabledModules = res.data.filter(
        (m) => m.role === "parent" && m.enabled
      );

      setModules(enabledModules);

    } catch (err) {
      console.error("Error fetching modules:", err);
    }
  };

  fetchModules();

}, [token, schoolId]);

  const handleNavigate = (path) => {
    const finalPath = path.replace(":id", studentId);
    navigate(finalPath);
  };

  const student = {
    name: "John Doe",
    rollNo: "23",
    bloodGroup: "B+",
    attendance: 92,
    photo: studentPhoto,
    attendanceRecord: [
      { date: "2025-08-18", status: "present" },
      { date: "2025-08-19", status: "absent" },
      { date: "2025-08-20", status: "present" },
      { date: "2025-08-21", status: "holiday" },
      { date: "2025-08-22", status: "present" },
    ],
    todayClasses: [
      { subject: "Math", teacher: "Mr. Sharma", timing: "09:00 - 10:00" },
      { subject: "Science", teacher: "Ms. Mehta", timing: "10:15 - 11:15" },
      { subject: "English", teacher: "Mr. Singh", timing: "11:30 - 12:30" },
    ],
  };

  const attendancePieData = [
    { name: "Present", value: student.attendance },
    { name: "Absent", value: 100 - student.attendance },
  ];
  const COLORS = ["#08e45cff", "#ef4444"];

  return (
    <>
      <ParentPanel onToggle={(collapsed) => setIsSidebarCollapsed(collapsed)} />

      <div className={`parent-main-content ${isSidebarCollapsed ? "collapsed" : ""}`}>
        {/* Top Bar with Notification */}
        <div className="parent-topbar">
          <span className="parent-topbar-left">TODAY IS {new Date().toLocaleString()}</span>
          <div className="parent-topbar-right">
            <span className="parent-school-name">Semigon Learning</span>
            <div
              className="parent-notification-wrapper"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <FaBell className="parent-notification-icon" />
              {notifications.length > 0 && (
                <span className="parent-notification-count">{notifications.length}</span>
              )}
              {showNotifications && (
                <div className="parent-notification-dropdown-card">
                  {notifications.map((note) => (
                    <div key={note.id} className="parent-notification-item">
                      {note.text}
                    </div>
                  ))}
                  {notifications.length === 0 && (
                    <div className="parent-notification-item">No notifications</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Student Photo */}
        <div className="parent-student-photo-container">
          <div className="parent-student-photo-square">
            <img src={student.photo} alt="Student" className="parent-student-photo-top" />
          </div>
        </div>

        {/* Student Info Card */}
        <div className="parent-student-details-card">
          <div className="parent-student-info-row">
            <div className="parent-student-info-item">
              <span className="parent-info-label">Name:</span>
              <span className="parent-info-value">{student.name}</span>
            </div>
            <div className="parent-student-info-item">
              <span className="parent-info-label">Roll No:</span>
              <span className="parent-info-value">{student.rollNo}</span>
            </div>
            <div className="parent-student-info-item">
              <span className="parent-info-label">Blood Group:</span>
              <span className="parent-info-value">{student.bloodGroup}</span>
            </div>
          </div>
        </div>

        {/* Quick Links - updated */}
        <h3 className="parent-dashboard-title"><FaLink style={{ marginRight: "8px" }} /> Quick Links</h3>
        <div className="parent-quick-links">
          {modules.map((mod, index) => (
  <div
    key={`${mod.module_id || mod.name}-${index}`}
              className="parent-quick-link-card"
              onClick={() => handleNavigate(mod.path)}
              style={{
                cursor: "pointer",
                backgroundColor: colorMap[mod.name] || "#607d8b",
              }}
            >
              <div className="parent-ql-left">
                <div className="parent-ql-count">-</div>
                <div className="parent-ql-label">{mod.name}</div>
              </div>
              <div className="parent-ql-icon-chip">
                <img
                  src={iconMap[mod.name] || dashboardIcon}
                  alt={mod.name}
                  style={{ width: "24px", height: "24px" }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Cards Row */}
        <div className="parent-cards-row">
          {/* Attendance Overview */}
          <div className="parent-dashboard-card">
            <h3>Attendance Overview</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={attendancePieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={60}
                  label
                >
                  {attendancePieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Today's Classes */}
          <div className="parent-dashboard-card parent-today-classes-card">
            <h3>Today's Classes</h3>
            <table className="parent-today-classes-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Teacher</th>
                  <th>Timing</th>
                </tr>
              </thead>
              <tbody>
                {student.todayClasses.map((cls, idx) => (
                  <tr key={idx}>
                    <td>{cls.subject}</td>
                    <td>{cls.teacher}</td>
                    <td>{cls.timing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Attendance Calendar & Report Card */}
        <div className="parent-cards-row">
          {/* Attendance Calendar */}
          <div className="parent-dashboard-card parent-half-width-card parent-attendance-calendar-card">
            <h3>Attendance Calendar</h3>
            <div className="parent-attendance-calendar-wrapper">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                modifiers={{
                  present: [new Date(2025, 7, 18), new Date(2025, 7, 20), new Date(2025, 7, 22)],
                  absent: [new Date(2025, 7, 19)],
                  holiday: [new Date(2025, 7, 21)],
                }}
                modifiersClassNames={{
                  present: "bg-green-500 text-white rounded-full",
                  absent: "bg-red-500 text-white rounded-full",
                  holiday: "bg-yellow-400 text-black rounded-full",
                }}
              />
            </div>
            <div className="parent-calendar-legend">
              <span className="parent-present-day">Present</span>
              <span className="parent-absent-day">Absent</span>
              <span className="parent-holiday-day">Holiday</span>
            </div>
          </div>

          {/* Report Card */}
          <div className="parent-dashboard-card parent-half-width-card parent-report-card">
            <h3>Report Card</h3>
            <table className="parent-today-classes-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Term 1</th>
                  <th>Term 2</th>
                  <th>Term 3</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Math</td>
                  <td>88</td>
                  <td>92</td>
                  <td>90</td>
                </tr>
                <tr>
                  <td>Science</td>
                  <td>85</td>
                  <td>89</td>
                  <td>91</td>
                </tr>
                <tr>
                  <td>English</td>
                  <td>90</td>
                  <td>87</td>
                  <td>88</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Blog Section */}
        <div className="parent-blog-section">
          <h3>School Blog & Updates</h3>
          <div className="parent-school-photo-container">
            <img src={schoolPhoto} alt="School" className="parent-school-photo" />
          </div>
          <div className="parent-school-articles">
            <div className="parent-article-card">
              <h4>New STEM Lab Inauguration</h4>
              <p>Our school has inaugurated a state-of-the-art STEM lab for students...</p>
            </div>
            <div className="parent-article-card">
              <h4>Sports Week Highlights</h4>
              <p>Last week’s sports week saw outstanding performances from our students...</p>
            </div>
            <div className="parent-article-card">
              <h4>Art & Culture Festival</h4>
              <p>The annual art and culture festival showcased amazing creativity...</p>
            </div>
          </div>

          <div className="parent-school-awards">
            <h4>Award Functions & Achievements</h4>
            <p>Our students excel in academics, sports, and extracurricular activities...</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ParentsDashboard;
